import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import { adminService } from '../../services/adminService';
import { AnimatedIcon } from '../../components/icons';
import { Negotiation, ChatMessage, SurveyorProfile, RequestState } from '../../types';
import { toNumber } from '../../utils/number';

const STATUS_META: Record<string, { bg: string; color: string; label: string }> = {
  [RequestState.NEGOTIATING]: { bg: 'var(--sc-warning-bg)', color: 'var(--sc-warning-text)', label: 'Negotiating' },
  [RequestState.CONFIRMED]:   { bg: 'var(--sc-success-bg)', color: 'var(--sc-success-text)', label: 'Confirmed'   },
  [RequestState.CLOSED]:      { bg: 'var(--sc-divider)', color: 'var(--sc-text-secondary)', label: 'Closed'      },
  [RequestState.DISPUTED]:    { bg: 'var(--sc-error-bg)', color: 'var(--sc-error-text)', label: 'Disputed'    },
};

export const AdminNegotiationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [negotiation, setNegotiation] = useState<Negotiation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [surveyors, setSurveyors] = useState<SurveyorProfile[]>([]);
  const [reassignId, setReassignId] = useState('');
  const [reassignLoading, setReassignLoading] = useState(false);
  const [reassignMsg, setReassignMsg] = useState('');
  const [closeLoading, setCloseLoading] = useState(false);
  const [closeMsg, setCloseMsg] = useState('');
  const [blockingId, setBlockingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const listRef = useRef<HTMLDivElement>(null);

  const loadNegotiation = async () => {
    if (!id) return;
    const { data } = await apiClient.get(`/admin/negotiations/${id}/`);
    setNegotiation(data);
  };

  const loadMessages = async () => {
    if (!id) return;
    const { data } = await apiClient.get(`/admin/negotiations/${id}/messages/`, { params: { page_size: 200 } });
    setMessages(((data.results ?? data ?? []) as ChatMessage[]).slice().reverse());
  };

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    Promise.all([
      loadNegotiation(),
      loadMessages(),
      adminService.listSurveyors({ verification_status: 'APPROVED' }).then((r) => {
        setSurveyors(Array.isArray(r) ? r : r.results ?? []);
      }),
    ]).catch(() => {}).finally(() => setIsLoading(false));
  }, [id]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const handleReassign = async () => {
    if (!reassignId || !id) return;
    setReassignLoading(true);
    try {
      await apiClient.post(`/admin/negotiations/${id}/reassign/`, { surveyor_id: reassignId });
      setReassignMsg('✅ Surveyor reassigned successfully.');
      await loadNegotiation();
    } catch (e: any) {
      setReassignMsg(`❌ ${e?.response?.data?.detail || 'Reassignment failed.'}`);
    } finally {
      setReassignLoading(false);
    }
  };

  const handleClose = async () => {
    if (!id || !negotiation) return;
    const isOpen = negotiation.status === RequestState.NEGOTIATING;
    setCloseLoading(true);
    setCloseMsg('');
    try {
      const action = isOpen ? 'close' : 'reopen';
      await apiClient.post(`/admin/negotiations/${id}/${action}/`);
      setCloseMsg(`✅ Negotiation ${action}ed successfully.`);
      await loadNegotiation();
    } catch (e: any) {
      setCloseMsg(`❌ ${e?.response?.data?.detail || 'Action failed.'}`);
    } finally {
      setCloseLoading(false);
    }
  };

  const handleBlockMessage = async (msgId: string, currentlyBlocked: boolean) => {
    if (!id) return;
    setBlockingId(msgId);
    try {
      const action = currentlyBlocked ? 'unblock' : 'block';
      await apiClient.post(`/admin/negotiations/${id}/messages/${msgId}/${action}/`);
      setMessages((prev) =>
        prev.map((m) => (m.id === msgId ? { ...m, is_blocked: !currentlyBlocked } : m)),
      );
    } catch {} finally {
      setBlockingId(null);
    }
  };

  if (isLoading) return <div style={s.center}><div style={s.spinner} /></div>;
  if (!negotiation) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--sc-text-secondary)' }}>Negotiation not found.</div>;

  const sc = STATUS_META[negotiation.status] || { bg: 'var(--sc-bg-primary)', color: 'var(--sc-text-secondary)', label: negotiation.status };
  const isOpen = negotiation.status === RequestState.NEGOTIATING;
  const blockedCount = messages.filter((m) => m.is_blocked).length;

  return (
    <div>
      {/* Back + header */}
      <button style={s.back} onClick={() => navigate(-1)}>
        <AnimatedIcon name="folder" size={14} color="var(--sc-text-secondary)" /> Back to Negotiations
      </button>

      <div style={s.pageHeader}>
        <div>
          <h1 style={s.title}>
            Negotiation <span style={s.idChip}>#{negotiation.id.slice(0, 8)}</span>
          </h1>
          <p style={s.subtitle}>
            {negotiation.request?.service_type?.replace(/_/g, ' ')} · Started{' '}
            {new Date(negotiation.created_at).toLocaleDateString('en-ZW', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <span style={{ ...s.statusBadge, background: sc.bg, color: sc.color }}>{sc.label}</span>
      </div>

      <div style={s.grid}>
        {/* ── Left column ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Participants */}
          <div style={s.card}>
            <h3 style={s.cardTitle}><AnimatedIcon name="team" size={16} color="var(--sc-brand-primary)" /> Participants</h3>
            {[
              { label: 'Client',   user: negotiation.client,             color: 'var(--sc-brand-primary)' },
              { label: 'Surveyor', user: negotiation.surveyor as any,    color: '#8B5CF6' },
            ].map(({ label, user, color }) => (
              <div key={label} style={s.participantRow}>
                <div style={{ ...s.avatar, background: color }}>
                  {(user?.first_name?.[0] || '?').toUpperCase()}{(user?.last_name?.[0] || '').toUpperCase()}
                </div>
                <div>
                  <div style={s.participantRole}>{label}</div>
                  <div style={s.participantName}>{user?.first_name} {user?.last_name}</div>
                  <div style={s.participantSub}>{user?.phone}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Request + Deal Info */}
          <div style={s.card}>
            <h3 style={s.cardTitle}><AnimatedIcon name="clipboard" size={16} color="var(--sc-brand-primary)" /> Request &amp; Deal</h3>
            <div style={s.infoGrid}>
              {[
                { label: 'Service',  value: negotiation.request?.service_type?.replace(/_/g, ' ') },
                { label: 'Location', value: negotiation.request?.address },
                { label: 'Started',  value: new Date(negotiation.created_at).toLocaleString('en-ZW') },
                { label: 'Updated',  value: new Date(negotiation.updated_at).toLocaleString('en-ZW') },
              ].map(({ label, value }) => (
                <div key={label} style={s.infoItem}>
                  <div style={s.infoLabel}>{label}</div>
                  <div style={s.infoValue}>{value || '—'}</div>
                </div>
              ))}
            </div>

            {/* Deal value highlight */}
            {negotiation.deal_value != null && (
              <div style={s.dealBox}>
                <div style={s.dealRow}>
                  <span style={s.dealLabel}>Agreed Deal Value</span>
                  <span style={s.dealValue}>${negotiation.deal_value.toLocaleString()}</span>
                </div>
                <div style={s.dealRow}>
                  <span style={s.dealLabel}>Platform Commission (12%)</span>
                  <span style={{ ...s.dealValue, color: 'var(--sc-error)' }}>
                    ${(negotiation.deal_value * 0.12).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Admin Actions */}
          <div style={s.card}>
            <h3 style={s.cardTitle}><AnimatedIcon name="settings" size={16} color="var(--sc-brand-primary)" /> Admin Actions</h3>

            {/* Close / Reopen */}
            <p style={s.actionHint}>
              {isOpen ? 'Close this negotiation to prevent further messages.' : 'Reopen this negotiation to allow chat to resume.'}
            </p>
            {closeMsg && (
              <div style={{ ...s.feedbackBox, background: closeMsg.startsWith('✅') ? 'var(--sc-success-bg)' : 'var(--sc-error-bg)', color: closeMsg.startsWith('✅') ? 'var(--sc-success-text)' : 'var(--sc-error-text)', border: `1px solid ${closeMsg.startsWith('✅') ? 'var(--sc-success)' : 'var(--sc-error)'}` }}>
                {closeMsg}
              </div>
            )}
            <button
              onClick={handleClose}
              disabled={closeLoading}
              style={{ ...s.actionBtn, background: isOpen ? 'var(--sc-error)' : 'var(--sc-success)', marginBottom: 20 }}
            >
              {closeLoading ? 'Processing…' : isOpen ? '🔒 Close Negotiation' : '🔓 Reopen Negotiation'}
            </button>

            {/* Reassign */}
            <p style={s.actionHint}>Reassign a different surveyor to this negotiation.</p>
            {reassignMsg && (
              <div style={{ ...s.feedbackBox, background: reassignMsg.startsWith('✅') ? 'var(--sc-success-bg)' : 'var(--sc-error-bg)', color: reassignMsg.startsWith('✅') ? 'var(--sc-success-text)' : 'var(--sc-error-text)', border: `1px solid ${reassignMsg.startsWith('✅') ? 'var(--sc-success)' : 'var(--sc-error)'}` }}>
                {reassignMsg}
              </div>
            )}
            <select value={reassignId} onChange={(e) => setReassignId(e.target.value)} style={s.select}>
              <option value="">Select replacement surveyor…</option>
              {surveyors
                .filter((sv) => sv.id !== (negotiation.surveyor as any)?.id)
                .map((sv) => (
                  <option key={sv.id} value={sv.id}>
                    {sv.first_name} {sv.last_name} — {sv.base_city} · ⭐{toNumber(sv.rating_average).toFixed(1)}
                  </option>
                ))}
            </select>
            <button
              onClick={handleReassign}
              disabled={!reassignId || reassignLoading}
              style={{ ...s.actionBtn, background: reassignId ? 'var(--sc-warning)' : 'var(--sc-border)', cursor: reassignId ? 'pointer' : 'not-allowed' }}
            >
              <AnimatedIcon name="worker" size={16} color="#FFF" />
              {reassignLoading ? 'Reassigning…' : 'Reassign Surveyor'}
            </button>
          </div>

        </div>

        {/* ── Right column: Chat ── */}
        <div style={{ ...s.card, display: 'flex', flexDirection: 'column' as const, height: 600 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <h3 style={s.cardTitle}><AnimatedIcon name="chat" size={16} color="var(--sc-brand-primary)" /> Chat Transcript</h3>
            {blockedCount > 0 && (
              <span style={s.blockedBadge}>{blockedCount} blocked</span>
            )}
          </div>
          <div style={s.chatMeta}>Read-only view · {messages.length} message{messages.length !== 1 ? 's' : ''}</div>

          <div ref={listRef} style={s.messageList}>
            {messages.length === 0 && (
              <div style={s.noMessages}>
                <div style={s.noMsgIcon}><AnimatedIcon name="chat" size={28} color="var(--sc-text-tertiary)" /></div>
                <div>No messages yet.</div>
              </div>
            )}
            {messages.map((msg) => {
              if (msg.message_type === 'SYSTEM') {
                return (
                  <div key={msg.id} style={s.systemMsg}>
                    <span style={s.systemDivider} />
                    <span style={s.systemText}>{msg.content}</span>
                    <span style={s.systemDivider} />
                  </div>
                );
              }
              const isClient = msg.sender?.id === negotiation.client?.id;
              return (
                <div key={msg.id} style={{ ...s.msgRow, flexDirection: isClient ? 'row-reverse' : 'row' as const }}>
                  <div style={{ ...s.msgAvatar, background: isClient ? 'var(--sc-brand-primary)' : '#8B5CF6' }}>
                    {(msg.sender?.first_name?.[0] || '?').toUpperCase()}
                  </div>
                  <div style={{ maxWidth: '72%', flex: 1, minWidth: 0 }}>
                    <div style={{ ...s.msgMeta, textAlign: isClient ? 'right' : 'left' as const }}>
                      {msg.sender?.first_name} · {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <div
                      style={{
                        ...s.msgBubble,
                        background:   msg.is_blocked ? 'var(--sc-error-bg)' : isClient ? 'var(--sc-bg-active)' : 'var(--sc-bg-primary)',
                        color:        msg.is_blocked ? 'var(--sc-error-text)' : 'var(--sc-text-primary)',
                        border:       msg.is_blocked ? '1px solid var(--sc-error)' : '1px solid transparent',
                        borderRadius: isClient ? '14px 4px 14px 14px' : '4px 14px 14px 14px',
                        opacity:      msg.is_blocked ? 0.8 : 1,
                      }}
                    >
                      {msg.is_blocked ? (
                        <span>
                          <AnimatedIcon name="warning" size={12} color="var(--sc-error)" />{' '}
                          <span style={{ textDecoration: 'line-through' }}>{msg.content}</span>
                          {msg.blocked_reason && (
                            <div style={{ fontSize: 11, marginTop: 4, textDecoration: 'none', fontStyle: 'italic' }}>
                              Reason: {msg.blocked_reason}
                            </div>
                          )}
                        </span>
                      ) : msg.content}
                    </div>
                    {/* Block / Unblock button */}
                    <div style={{ textAlign: isClient ? 'right' : 'left' as const, marginTop: 2 }}>
                      <button
                        onClick={() => handleBlockMessage(msg.id, msg.is_blocked)}
                        disabled={blockingId === msg.id}
                        style={{
                          ...s.blockBtn,
                          color: msg.is_blocked ? 'var(--sc-success)' : 'var(--sc-error)',
                          borderColor: msg.is_blocked ? 'var(--sc-success)' : 'var(--sc-error)',
                          background: msg.is_blocked ? 'var(--sc-success-bg)' : 'var(--sc-error-bg)',
                        }}
                      >
                        {blockingId === msg.id ? '…' : msg.is_blocked ? '✓ Unblock' : '⊘ Block'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  center:  { display: 'flex', justifyContent: 'center', padding: 60 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },

  back:       { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', fontSize: 13, color: 'var(--sc-text-secondary)', cursor: 'pointer', padding: 0, marginBottom: 16, fontFamily: 'inherit' },
  pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, gap: 12, flexWrap: 'wrap' },
  title:      { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' as const, fontFamily: "'Outfit', sans-serif" },
  idChip:     { background: 'var(--sc-bg-primary)', color: 'var(--sc-text-secondary)', fontSize: 16, fontWeight: 700, borderRadius: 8, padding: '3px 10px', fontFamily: 'monospace' },
  subtitle:   { fontSize: 13, color: 'var(--sc-text-secondary)', margin: 0 },
  statusBadge:{ padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700, flexShrink: 0, marginTop: 4 },

  grid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, alignItems: 'start' },
  card:    { background: 'var(--sc-bg-secondary)', borderRadius: 14, padding: 22, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-md)' },
  cardTitle:{ fontSize: 14, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8, fontFamily: "'Outfit', sans-serif" },

  participantRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 },
  avatar:         { width: 40, height: 40, borderRadius: '50%', color: 'var(--sc-text-inverse)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 },
  participantRole:{ fontSize: 10, fontWeight: 700, color: 'var(--sc-text-tertiary)', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: 1 },
  participantName:{ fontWeight: 700, color: 'var(--sc-text-primary)', fontSize: 14 },
  participantSub: { fontSize: 12, color: 'var(--sc-text-secondary)' },

  infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  infoItem: { },
  infoLabel:{ fontSize: 10, fontWeight: 700, color: 'var(--sc-text-tertiary)', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: 2 },
  infoValue:{ fontSize: 13, color: 'var(--sc-text-primary)', fontWeight: 500 },

  dealBox:  { marginTop: 16, background: 'var(--sc-warning-bg)', borderRadius: 10, padding: '12px 14px', border: '1px solid var(--sc-warning)' },
  dealRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  dealLabel:{ fontSize: 12, color: 'var(--sc-warning-text)', fontWeight: 500 },
  dealValue:{ fontSize: 15, fontWeight: 800, color: 'var(--sc-warning-text)' },

  actionHint:   { fontSize: 12, color: 'var(--sc-text-secondary)', margin: '0 0 10px' },
  feedbackBox:  { padding: '10px 14px', borderRadius: 10, fontSize: 13, fontWeight: 600, marginBottom: 12 },
  actionBtn:    { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '11px', color: '#FFF', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', transition: 'all 0.2s' },
  select:       { width: '100%', padding: '10px 12px', border: '1px solid var(--sc-border)', borderRadius: 10, fontSize: 13, marginBottom: 10, background: 'var(--sc-bg-input)', color: 'var(--sc-text-primary)', fontFamily: 'inherit', outline: 'none' },

  blockedBadge: { background: 'var(--sc-error-bg)', color: 'var(--sc-error-text)', fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '3px 10px', border: '1px solid var(--sc-error)' },
  blockBtn:     { fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12, border: '1px solid', cursor: 'pointer', background: 'transparent', fontFamily: 'inherit', transition: 'all var(--sc-transition-fast)' },

  chatMeta:    { fontSize: 11, color: 'var(--sc-text-tertiary)', marginTop: -10, marginBottom: 14 },
  messageList: { flex: 1, overflowY: 'auto' as const, paddingRight: 4, display: 'flex', flexDirection: 'column' as const, gap: 12 },
  msgRow:      { display: 'flex', alignItems: 'flex-start', gap: 8 },
  msgAvatar:   { width: 30, height: 30, borderRadius: '50%', color: 'var(--sc-text-inverse)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, flexShrink: 0 },
  msgMeta:     { fontSize: 11, color: 'var(--sc-text-tertiary)', marginBottom: 3 },
  msgBubble:   { padding: '10px 14px', fontSize: 13, lineHeight: 1.5 },
  systemMsg:   { display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' },
  systemDivider:{ flex: 1, height: 1, background: 'var(--sc-border)' },
  systemText:  { fontSize: 11, color: 'var(--sc-text-tertiary)', whiteSpace: 'nowrap' as const, flexShrink: 0 },
  noMessages:  { flex: 1, display: 'flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'center', color: 'var(--sc-text-tertiary)', fontSize: 13, gap: 10, padding: 30 },
  noMsgIcon:   { width: 60, height: 60, borderRadius: '50%', background: 'var(--sc-bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
};
